import { CorporationDataType } from '@commenda/types/src/corporation';
import {
  DirectorBase,
  TValidateStakeholdersResponse,
} from '@commenda/types/src/incorporation/types';
import { getCorporationName } from '@commenda/utils';
import { LegacyDocumentType, StakeholderType } from '@prisma/client';
import { ZodError } from 'zod';
import { corporationDetailsSchema } from '../../common/corporateStakeholder/CorporationDetailsForm';
import { getDirectorName } from '../../Director/common';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';
import { checkEmailValidityForDirector } from '../../../common/helpers';
import { getIdentityProof } from '../utils';
import { directorSchema } from './schema';

const validateDirectorInfo = (
  directors: DirectorBase[],
  selectedDirectorIds: number[],
  corporationId: number
) => {
  const directorWithError = [];
  const selectedDirectors = directors?.filter((director) =>
    selectedDirectorIds.includes(director.id)
  );

  for (let i = 0; i < selectedDirectors.length; i += 1) {
    const identityProofFile = getIdentityProof(
      selectedDirectors[i],
      corporationId
    );
    const passportSizePhotoFile = selectedDirectors[i]?.directorFiles
      ?.sort((a, b) => b.id - a.id)
      .find(
        (eachFile) =>
          eachFile.documentType === LegacyDocumentType.PassportSizePhoto
      );

    const specimenSignatureFile = selectedDirectors[i]?.directorFiles
      ?.sort((a, b) => b.id - a.id)
      .find(
        (eachFile) =>
          eachFile.documentType === LegacyDocumentType.SpecimenSignature
      );
    const validationResult = directorSchema.safeParse({
      ...selectedDirectors[i],
      identityProof: identityProofFile,
      passportSizePhoto: passportSizePhotoFile,
      specimenSignature: specimenSignatureFile,
    }) as {
      success: boolean;
      error: any;
    };

    if (!validationResult.success) {
      if (validationResult.error) {
        directorWithError.push({
          name: getDirectorName(selectedDirectors[i]),
          id: selectedDirectors[i]?.id,
        });
      }
    }
  }

  return directorWithError;
};

const validateIndianPvtLtdStakeholders = (
  selectedStakeholders: TShareholderVsSharePercentageMap,
  {
    directors,
    corporations,
    corporationId,
  }: {
    directors: DirectorBase[];
    corporations: CorporationDataType[];
    corporationId: number;
  }
): TValidateStakeholdersResponse[] => {
  const shareholderWithError: TValidateStakeholdersResponse[] = [];
  const emailConflicts = []; // Track email conflicts separately

  // Find the company email from the corporations array
  const currentCorporation = corporations.find(corp => corp.id === corporationId);
  const companyEmail = currentCorporation?.contactEmail;

  // Filter directors to only include those belonging to THIS PVT corporation
  const pvtDirectors = directors.filter((dir) =>
    dir.corporationHasDirector?.find(
      (dirWithCorp) =>
        dirWithCorp.corporationId === corporationId &&
        dirWithCorp.additionalDirectorInformation?.isDirector === true
    )
  );

  // Use ONLY PVT-specific directors for email uniqueness validation
  const companyEmailLower = companyEmail?.trim().toLowerCase();
  const pvtDirectorEmails = pvtDirectors?.map(d => d.email?.trim().toLowerCase()).filter(Boolean) || [];

  Object.keys(selectedStakeholders).forEach((stakeholderId) => {
    const [type, id] = stakeholderId.split('_');

    if (type === StakeholderType.INDIVIDUAL) {
      const director = directors.find((d) => d.id === +id);

      // Check email uniqueness for individual stakeholders (against PVT directors only)
      if (director) {
        const isValid = checkEmailValidityForDirector(
          director,
          getDirectorName(director),
          stakeholderId,
          companyEmailLower,
          pvtDirectorEmails,
          pvtDirectors,
          emailConflicts,
          shareholderWithError,
          getDirectorName
        );
        
        if (!isValid) return;
      }

      const directorWithError = validateDirectorInfo(
        directors,
        [director.id],
        corporationId
      );

      if (directorWithError.length > 0) {
        shareholderWithError.push({
          name: getDirectorName(director),
          id: stakeholderId,
        });
      }
    }

    if (type === StakeholderType.INSTITUTION) {
      const corporation = corporations.find((c) => c.id === +id);

      const corporationBasicInformationValidationResult =
        corporationDetailsSchema.safeParse(corporation) as {
          success: boolean;
          error: ZodError;
        };

      if (!corporationBasicInformationValidationResult.success) {
        if (corporationBasicInformationValidationResult.error) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      }

      const authorizedSignatory = corporation.corporationHasDirector?.find(
        (director) =>
          director.additionalDirectorInformation?.isAuthorizedSignatory
      );

      if (authorizedSignatory) {
        // Find the director data for the authorized signatory
        const signatoryDirector = directors.find(d => d.id === authorizedSignatory.directorId);
        
        // Check email uniqueness for authorized signatory (against PVT directors only)
        if (signatoryDirector) {
          const isValid = checkEmailValidityForDirector(
            signatoryDirector,
            getCorporationName(corporation),
            stakeholderId,
            companyEmailLower,
            pvtDirectorEmails,
            pvtDirectors,
            emailConflicts,
            shareholderWithError,
            getDirectorName
          );
          
          if (!isValid) return;
        }

        const directorValidationResult = validateDirectorInfo(
          directors,
          [authorizedSignatory.directorId],
          corporationId
        );

        if (directorValidationResult.length > 0) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      } else {
        shareholderWithError.push({
          name: getCorporationName(corporation),
          id: stakeholderId,
        });
      }
    }
  });

  // Generate detailed error message if there are email conflicts
  if (emailConflicts.length > 0) {
    const emailErrorMessage = emailConflicts.map(conflict => {
      if (conflict.conflictType === 'company') {
        return `${conflict.stakeholderName} (${conflict.stakeholderEmail}) has the same email as the company.`;
      } else {
        return `${conflict.stakeholderName} (${conflict.stakeholderEmail}) has the same email as ${conflict.conflictTarget}.`;
      }
    }).join(' ');

    // Store the detailed error message on the result
    (shareholderWithError as any).emailErrorMessage = `Email conflicts found: ${emailErrorMessage}`;
  }

  return shareholderWithError;
};

export { validateDirectorInfo, validateIndianPvtLtdStakeholders };
